"use client";

import { Core } from "@faire/design-tokens";
import { isEnumKey } from "@faire/web/common/typescriptUtils";
import { DefaultTheme } from "@faire/web/slate/Theme";
import {
  Typography,
  TypographyProps,
  TypographyTags,
} from "@faire/web/slate/Typography";
import React from "react";
import styled, { css } from "styled-components";

import {
  createFallbackHandler,
  FallbackOpacityAnimation,
  IFallbackTreatment,
} from "./FallbackHandler";

interface IProps extends TypographyProps {
  children?: React.ReactNode | undefined;
  asTag?: TypographyTags; // The tag rendered to the dom
  fallback?: string;
  isLoaded: boolean;
  className?: string;
  loadingInsetTop?: number;
  loadingInsetBottom?: number;
  loadingWidth?: string;
}

export const TypographyGhostWithOpacityStyle = (
  duration: string
): IFallbackTreatment => {
  return css`
    background-color: ${Core.surface.muted};
    // Set the color to be the same as the background color so that it is
    // visually indistinguishable from the background and looks like it isn't present
    color: ${Core.surface.muted};
    ${FallbackOpacityAnimation(duration)}
  `;
};

const LoadableTypographyHandler = createFallbackHandler(Typography, {
  backgroundStyle: TypographyGhostWithOpacityStyle("0.2s"),
  loadedStyle: FallbackOpacityAnimation("0.2s"),
});

const StyledLoadableTypographyHandler = styled(LoadableTypographyHandler)<{
  isLoaded?: boolean;
  asTag?: TypographyTags;
  loadingWidth?: string;
  $loadingInsetTop?: number;
  $loadingInsetBottom?: number;
  $lineHeight?: string;
}>`
  ${(props) =>
    !props.isLoaded &&
    css`
      ${props.$lineHeight &&
      css`
        line-height: ${props.$lineHeight};
      `};
      margin: ${props.$loadingInsetTop || 0}px 0
        ${props.$loadingInsetBottom || 0}px;
      width: ${props.loadingWidth ?? "auto"};
    `}
`;

export const LoadableTypography: React.FC<IProps> = (props) => {
  const {
    fallback,
    children,
    isLoaded,
    asTag,
    loadingInsetBottom,
    loadingInsetTop,
    ...rest
  } = props;

  const lineHeight = (() => {
    const fonts = DefaultTheme.fonts.sansSerif;
    const getNumFromPx = (str: string) => str.substring(0, str.length - 2);
    let variant: keyof typeof fonts = "p";
    for (const prop in props) {
      if (isEnumKey(fonts, prop)) {
        variant = prop;
      }
    }
    const currentLineHeight = getNumFromPx(fonts[variant].lineHeight);
    if (
      typeof loadingInsetTop === "number" ||
      typeof loadingInsetBottom === "number"
    ) {
      return `calc(${currentLineHeight} - ${calculateInset(
        loadingInsetTop,
        loadingInsetBottom
      )}px)`;
    }
    return undefined;
  })();

  return (
    <StyledLoadableTypographyHandler
      {...rest}
      asTag={asTag}
      isLoaded={isLoaded}
      $lineHeight={lineHeight}
      $loadingInsetTop={loadingInsetTop}
      $loadingInsetBottom={loadingInsetBottom}
    >
      {isLoaded ? children : fallback ?? children}
    </StyledLoadableTypographyHandler>
  );
};

const calculateInset = (top?: number, bottom?: number) =>
  (top ?? 0) + (bottom ?? 0);
